import { useState, useEffect, useCallback, useMemo } from "react";
import { useConfigView } from "../../Config.context";
import { usePostAccountInsightsFindFilterQuery } from "../../app/useGeneratedApi";
import { useRefreshRegister } from "../../components/RefreshProvider";
import {
  AccountInsightView,
  AccountInsightsFilterView,
  AccountInsightsResponseView,
} from "../../app/GeneratedApi";
import { formatDate } from "../../utils/dateUtils";

export interface FormattedInsight {
  id: string;
  name: string;
  type: "CAMPAIGN" | "WORKFLOW" | "FORM" | "AUDIENCE" | "SEGMENT";
  status: string;
  timestamp: string;
  eventTime: number;
  creationSource?: "AI" | "API" | "UI" | null;
}

const PAGE_SIZE = 10;

export const useAccountInsightsPanel = () => {
  const { config } = useConfigView();
  const navigate = config?.navigateTo;

  const [tabValue, setTabValue] = useState(0);
  const [insights, setInsights] = useState<FormattedInsight[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const tabLabels = useMemo(
    () =>
      config?.appName === "tinyEmail"
        ? ["All", "CAMPAIGN", "WORKFLOW", "FORM", "AUDIENCE", "SEGMENT"]
        : ["All", "CAMPAIGN", "WORKFLOW", "FORM", "AUDIENCE"],
    [config?.appName]
  );

  const filterTypes =
    useMemo((): AccountInsightView["accountInsightsType"][] => {
      if (tabValue === 0) {
        return tabLabels.slice(
          1
        ) as AccountInsightView["accountInsightsType"][];
      }
      return [tabLabels[tabValue] as AccountInsightView["accountInsightsType"]];
    }, [tabValue, tabLabels]);

  const filterData: AccountInsightsFilterView = useMemo(
    () => ({
      accountInsightsTypes: filterTypes,
    }),
    [filterTypes]
  );

  const {
    data: insightsData,
    isLoading,
    refetch: refetchInsights,
    isFetching,
  } = usePostAccountInsightsFindFilterQuery(
    filterData,
    {
      page,
      size: PAGE_SIZE,
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    }
  );

  useRefreshRegister(async () => {
    setPage(0);
    setHasMore(true);
    setInsights([]);
    const result = await refetchInsights();
    if (result.data) formatInsights(result.data);
  });

  const formatInsights = (insightsData: AccountInsightsResponseView) => {
    if (!insightsData?.content) return;

    const formattedInsights: FormattedInsight[] = insightsData.content.map(
      (insight: AccountInsightView) => ({
        id: insight.id,
        name: insight.name || "Unnamed",
        type: insight.accountInsightsType,
        status: insight.eventType,
        timestamp: formatDate(insight.eventTime),
        eventTime: insight.eventTime,
        creationSource: insight.creationSource,
      })
    );

    setInsights((prev) =>
      page === 0 ? formattedInsights : [...prev, ...formattedInsights]
    );
    setHasMore(insightsData.totalElements !== 0);
  };

  useEffect(() => {
    if (insightsData) {
      formatInsights(insightsData);
    }
  }, [insightsData]);

  useEffect(() => {
    setPage(0);
    setHasMore(true);
    setInsights([]);
  }, [tabValue]);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number): void => {
      setTabValue(newValue);
    },
    []
  );

  const handleInsightClick = useCallback(
    (insight: FormattedInsight): void => {
      if (!navigate) {
        console.warn("Navigate function is not available.");
        return;
      }

      const { id, type, status, name } = insight;
      const isTinyEmail = config?.appName === "tinyEmail";
      let url = "";

      if (isTinyEmail) {
        switch (type) {
          case "CAMPAIGN":
            if (status !== "COMPLETED") {
              url = `/campaign/${id}/`;
            } else {
              url = `/main/campaigns/${id}/`;
            }
            break;
          case "WORKFLOW":
            url = `/workflow/${id}`;
            break;
          case "FORM":
            url = `/form/${id}/editor/`;
            break;
          case "AUDIENCE":
            url = `/main/audience/sources/${id}/`;
            break;
          case "SEGMENT":
            url = `/main/audience/segments/${id}/`;
            break;
          default:
            console.warn(`Unhandled insight type for tinyEmail: ${type}`);
            return;
        }
      } else {
        switch (type) {
          case "CAMPAIGN":
            url =
              status === "COMPLETED"
                ? `/main/campaigns/overview/past-campaigns?searchTextName=${name}&page=1`
                : `/main/campaigns/overview/future-campaigns?searchTextName=${name}&page=1`;
            break;
          case "WORKFLOW":
            url = `/workflow/${id}`;
            break;
          case "FORM":
            url = `/main/forms`;
            break;
          case "AUDIENCE":
            url = `/main/audience?sort=name,asc`;
            break;
          default:
            console.warn(`Unhandled insight type for non-tinyEmail: ${type}`);
            return;
        }
      }

      navigate(url);
    },
    [navigate, config?.appName]
  );

  const handleRefresh = useCallback(async (): Promise<void> => {
    setInsights([]);
    setPage(0);
    setHasMore(true);
    const result = await refetchInsights();
    if (result.data) formatInsights(result.data);
  }, [refetchInsights]);

  const loadMore = useCallback(() => {
    if (!isFetching && hasMore) {
      setPage((prev) => prev + 1);
    }
  }, [isFetching, hasMore]);

  return {
    tabValue,
    handleTabChange,
    insights,
    isLoading,
    isFetching,
    hasMore,
    handleRefresh,
    handleInsightClick,
    loadMore,
    tabLabels,
  };
};
